import '../style.css';

import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { Modal, Button, Spinner, Group } from '@economic/gaudi';
import { log } from '@economic/react-app';
import { Form, Field } from '../../../../../shared/components/Form';
import { RouteDisclosure } from '../../../../../shared/components/RouteDisclosure';
import { t, useLocalization } from '../../../../../shared/components/l10n/l10n';
import internalApiClient from '../../../../../shared/util/api/internalApiClient';
import { BulkDepartmentRequest, BulkDepartmentLocationState } from '../types';
import DepartmentCombobox from '../../../../../shared/components/ui/DepartmentCombobox';
import { Department } from '../../../../../views/Departments';
import useQuery from '../../../../../shared/hooks/useQuery';

interface BulkDepartmentProps {
    onSubmitted: () => void;
}

export function BulkDepartmentDialog({ onSubmitted }: BulkDepartmentProps) {
    const navigate = useNavigate();
    const location = useLocation();
    const { draftId, selectedLineIds } = (location.state || {}) as BulkDepartmentLocationState;
    const [loading, setLoading] = useState<boolean>();
    const textsLoading = useLocalization(textIds);
    const { loading: departmentsLoading, data: departments } = useQuery<Department[]>(
        `/departments?includeDistributionKeys=${true}`
    );

    const disclosure = <RouteDisclosure routeTrigger={'#bulk-department'} />;

    function navigateBack() {
        navigate(location.pathname);
    }

    const validateDepartmentNumber = (departments: Department[], schema, field) => {
        const department = departments.find(r => r.number === field.value);
        if (!department) {
            return schema.test('', t`GENERALINPUT_ERROR_DEPARTMENTANDNOONE`, () => false);
        } else {
            if (!department.access) {
                return schema.test('', t`GENERALINPUT_ERROR_DEPARTMENTISBARRED`, () => false);
            }
        }
        return schema;
    };

    const validationSchema = Yup.object({
        departmentNumber: Yup.number()
            .notRequired()
            .when('$departments', validateDepartmentNumber),
    });

    async function submit(request: BulkDepartmentRequest) {
        setLoading(true);
        try {
            await internalApiClient.post('/sales/invoicing/bulk-department', request);
        } catch (error) {
            if (error && error.response) {
                if (error.response.status >= 500) {
                    log.error(error as Error);
                }
            }
        }
        setLoading(false);
        navigateBack();
        onSubmitted();
    }

    async function clearDepartments() {
        setLoading(true);
        try {
            const request: BulkDepartmentRequest = { draftId, selectedLineIds };
            await internalApiClient.post('/sales/invoicing/bulk-department', request);
        } catch (error) {
            if (error && error.response) {
                if (error.response.status >= 500) {
                    log.error(error as Error);
                }
            }
        }
        setLoading(false);
        navigateBack();
        onSubmitted();
    }

    if (loading || textsLoading || departmentsLoading) {
        return (
            <Modal aria-label={t`NOTIFICATION_LOADING`} onHide={navigateBack} disclosure={disclosure}>
                <Modal.Content>
                    <Spinner />
                </Modal.Content>
            </Modal>
        );
    }

    if (!selectedLineIds || selectedLineIds.length === 0) {
        return (
            <Modal
                aria-label={t`FORM_BULKDEPARTMENT_ERROR_HEADER_CANNOTBULKDEPARTMENT`}
                onHide={navigateBack}
                disclosure={disclosure}>
                <Modal.Header>{t`FORM_BULKDEPARTMENT_ERROR_HEADER_CANNOTBULKDEPARTMENT`}</Modal.Header>
                <Modal.Content>
                    <p>{t`FORM_BULKDEPARTMENT_ERROR_DESCRIPTION_SELECTONELINEATLEAST`}</p>
                </Modal.Content>
                <Modal.Footer>
                    <Button appearance="primary" autoFocus onClick={navigateBack}>{t`SHARED_FORM_OK`}</Button>
                </Modal.Footer>
            </Modal>
        );
    }

    return (
        <Modal
            aria-label={t`FORM_BULKDEPARTMENT_HEADER`}
            onHide={navigateBack}
            disclosure={disclosure}
            className="bulk_department">
            <Form
                initialData={{ draftId, selectedLineIds }}
                onSubmit={submit}
                skipNotifications
                validationSchema={validationSchema}
                context={{ departments: departments }}>
                <Modal.Header>{t`FORM_BULKDEPARTMENT_HEADER`}</Modal.Header>
                <Modal.Content>
                    <div className="bulk_department__field_container">
                        <Field.Combobox
                            label={t`FORM_FIELD_LABEL_DEPARTMENT_NUMBER`}
                            name="departmentNumber"
                            as={DepartmentCombobox}
                        />
                    </div>
                </Modal.Content>
                <Modal.Footer className="justify-between">
                    <Button
                        appearance="ghost"
                        onClick={clearDepartments}>{t`FORM_BULKDEPARTMENT_BUTTON_CLEARDEPARTMENTS`}</Button>
                    <Group>
                        <Button appearance="discrete" onClick={navigateBack}>{t`FORM_BULKDEPARTMENT_BUTTON_CANCEL`}</Button>
                        <Button
                            appearance="primary"
                            type="submit"
                            onClick={e => {
                                (e.target as any).focus(); //fix for pressing enter not removing focus on input, thus not calculating value
                            }}>{t`FORM_BULKDEPARTMENT_BUTTON_ADD`}</Button>
                    </Group>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}

const textIds = [
    'NOTIFICATION_LOADING',
    'SHARED_FORM_OK',
    'FORM_BULKDEPARTMENT_ERROR_HEADER_CANNOTBULKDEPARTMENT',
    'FORM_BULKDEPARTMENT_ERROR_DESCRIPTION_SELECTONELINEATLEAST',
    'FORM_BULKDEPARTMENT_HEADER',
    'FORM_FIELD_LABEL_DEPARTMENT_NUMBER',
    'FORM_BULKDEPARTMENT_BUTTON_CANCEL',
    'FORM_BULKDEPARTMENT_BUTTON_ADD',
    'FORM_BULKDEPARTMENT_BUTTON_CLEARDEPARTMENTS',
    'GENERALINPUT_ERROR_DEPARTMENTANDNOONE',
    'GENERALINPUT_ERROR_DEPARTMENTISBARRED',
];
